/*
  predictive-search.js
  Reference link: https://shopify.dev/docs/themes/navigation-search/search/predictive-search
*/
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector(
        "#predictive-search-results-groups-wrapper"
      )?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = "";

      this.abortController.abort();
      this.abortController = new AbortController();

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;

      case "ArrowDown":
        this.switchOption("down");
        break;

      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === "ArrowUp" ||
      event.code === "ArrowDown"
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );

    const currentButtonText =
      searchForTextElement?.innerText;

    if (!currentButtonText || !previousTerm) {
      return;
    }

    /*
      Do not create a RegExp from customer input.

      Characters such as:
      (
      [
      +
      *
      ?

      could otherwise throw a JavaScript error and
      completely stop predictive search.
    */
    const firstMatch =
      currentButtonText.indexOf(previousTerm);

    const lastMatch =
      currentButtonText.lastIndexOf(previousTerm);

    if (
      firstMatch === -1 ||
      firstMatch !== lastMatch
    ) {
      return;
    }

    searchForTextElement.innerText =
      currentButtonText.slice(0, firstMatch) +
      newTerm +
      currentButtonText.slice(
        firstMatch + previousTerm.length
      );
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";

    const selectedElement = this.querySelector(
      '[aria-selected="true"]'
    );

    const allVisibleElements = Array.from(
      this.querySelectorAll(
        "li, button.predictive-search__item"
      )
    ).filter(
      (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) {
      return;
    }

    let selectedElementIndex = -1;
    let i = 0;

    while (
      selectedElementIndex === -1 &&
      i <= allVisibleElements.length
    ) {
      if (
        allVisibleElements[i] === selectedElement
      ) {
        selectedElementIndex = i;
      }

      i++;
    }

    if (this.statusElement) {
      this.statusElement.textContent = "";
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex ===
        allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (
      activeElementIndex === selectedElementIndex
    ) {
      return;
    }

    const activeElement =
      allVisibleElements[activeElementIndex];

    if (!activeElement) return;

    activeElement.setAttribute(
      "aria-selected",
      true
    );

    if (selectedElement) {
      selectedElement.setAttribute(
        "aria-selected",
        false
      );
    }

    this.input.setAttribute(
      "aria-activedescendant",
      activeElement.id
    );
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType =
      this.getSearchFilterType();

    const filterTypeQuery =
      this.getSearchFilterTypeQuery();

    const queryKey =
      searchTerm
        .replaceAll(" ", "-")
        .toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery
        .replaceAll(" ", "-")
        .toLowerCase();

    this.setLiveRegionLoadingState();

    /*
      Cancel the previous request whenever
      the customer types another character.

      This prevents an older request from
      finishing after a newer request and
      replacing the newest results.
    */
    this.abortController.abort();

    this.abortController =
      new AbortController();

    /*
      If we already fetched this exact
      search, use the cached result.
    */
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      return;
    }

    const limit = this.dataset.limit;
    const resultTypes = this.dataset.types;
    const showUnavailable =
      this.dataset.unavailable;

    const query = filterTypeQuery
      ? encodeURIComponent(
          `${filterType}:${filterTypeQuery}+* ${searchTerm}`
        )
      : encodeURIComponent(searchTerm);

    fetch(
      `${routes.predictive_search_url}?q=${query}&resources[type]=${resultTypes}&resources[limit]=${limit}&options[prefix]=last&options[unavailable_products]=${showUnavailable}&section_id=predictive-search`,
      {
        signal:
          this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          const error =
            new Error(response.status);

          this.close();

          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsedDocument =
          new DOMParser().parseFromString(
            text,
            "text/html"
          );

        const resultSection =
          parsedDocument.querySelector(
            "#shopify-section-predictive-search"
          );

        if (!resultSection) {
          throw new Error(
            "Predictive search response did not contain the expected section"
          );
        }

        const resultsMarkup =
          resultSection.innerHTML;

        /*
          Keep the cache synchronized between
          every predictive-search instance
          on the page.
        */
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[
              queryKey
            ] = resultsMarkup;
          }
        );

        this.renderSearchResults(
          resultsMarkup
        );
      })
      .catch((error) => {
        /*
          AbortError / code 20 simply means
          the customer typed something new
          before the previous request finished.
        */
        if (
          error?.name === "AbortError" ||
          error?.code === 20
        ) {
          return;
        }

        console.error(
          "Predictive search error:",
          error
        );

        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        ".predictive-search-status"
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        "data-loading-text"
      );

    if (this.statusElement) {
      this.setLiveRegionText(
        this.loadingText || ""
      );
    }

    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute(
      "aria-hidden",
      "false"
    );

    this.statusElement.textContent =
      statusText || "";

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute(
          "aria-hidden",
          "true"
        );
      }
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      return;
    }

    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      "results",
      "true"
    );

    this.setLiveRegionResults();

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");

    const countElement =
      this.querySelector(
        "[data-predictive-search-live-region-count-value]"
      );

    if (
      countElement &&
      this.statusElement
    ) {
      this.setLiveRegionText(
        countElement.textContent
      );
    }
  }

  getResultsMaxHeight() {
    const header =
      document.querySelector(
        ".header-default"
      );

    const headerBottom = header
      ? header
          .getBoundingClientRect()
          .bottom
      : 0;

    this.resultsMaxHeight = Math.max(
      0,
      window.innerHeight -
        headerBottom
    );

    return this.resultsMaxHeight;
  }

  open() {
    if (
      !this.predictiveSearchResults
    ) {
      return;
    }

    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight ||
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute("open", true);

    this.input.setAttribute(
      "aria-expanded",
      "true"
    );

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);

    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";

      this.removeAttribute("results");
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        "aria-selected",
        "false"
      );
    }

    this.input.setAttribute(
      "aria-activedescendant",
      ""
    );

    this.removeAttribute("loading");

    this.removeAttribute("open");

    this.input.setAttribute(
      "aria-expanded",
      "false"
    );

    this.resultsMaxHeight = false;

    if (
      this.predictiveSearchResults
    ) {
      this.predictiveSearchResults.removeAttribute(
        "style"
      );
    }
  }
}

customElements.define(
  "predictive-search",
  PredictiveSearch
);